import React, { useMemo } from 'react';
import DENTModalOverviewButtonsSelection from '../DENTModalOverviewButtonsSelection/DENTModalOverviewButtonsSelection';
import ToggleButtons from '../../../../Components/ToggleButtons/ToggleButtons';
import { useDENTContext } from '../../Services/DENTContext';
import { RestorationOption } from '../../Helpers/restoration/restorationButtonOptions';
import DENTModalOverviewRestorationFilling from './DENTModalOverviewRestorationFilling/DENTModalOverviewRestorationFilling';
import DENTModalOverviewRestorationVeneer from './DENTModalOverviewRestorationVeneer';
import DENTModalOverviewRestorationCrown from './DENTModalOverviewRestorationCrown';
import useTooth from '../../Hooks/Tooth/useTooth';
import {
	ToothRestorationType,
	ToothSurface,
} from '../../../../graphql/graphql';
import { SurfaceOperation } from '../../Enums/SurfaceOperation.enum';
import { PRESELECTED_RESTORATION_SURFACES } from '../DENTModalOverviewRestoration/restorationItemsSurfaces';

interface Props {
	selectedOption?: RestorationOption;
	setSelectedOption: (section: RestorationOption) => void;
	options: RestorationOption[];
	onClose: () => void;
	fillingIndex: number;
	setFillingIndex: (index: number) => void;
}

const DENTModalOverviewRestorationButtons = ({
	selectedOption,
	setSelectedOption,
	options,
	onClose,
	fillingIndex,
	setFillingIndex,
}: Props) => {
	const { activeTooth, originalActiveTooth } = useDENTContext();
	const {
		restoration: { setRestoration, setSurfaces },
	} = useTooth();

	const content: React.ReactNode = useMemo(() => {
		if (!activeTooth) return <></>;

		switch (selectedOption?.value) {
			case ToothRestorationType.Veneer:
			case ToothRestorationType.Onlay:
			case ToothRestorationType.Inlay:
			case ToothRestorationType.PartialCrown:
				return <DENTModalOverviewRestorationVeneer />;
			case ToothRestorationType.Filling:
				return (
					<DENTModalOverviewRestorationFilling
						fillingIndex={fillingIndex}
						setFillingIndex={setFillingIndex}
					/>
				);
			case ToothRestorationType.Crown:
				return <DENTModalOverviewRestorationCrown />;
			default:
				return <></>;
		}
	}, [selectedOption, activeTooth, fillingIndex, setFillingIndex]);

	const handleChangeOption = (option: RestorationOption) => {
		const isOriginalType =
			option.value === originalActiveTooth?.restoration?.type;

		const preselectedSurfaces = PRESELECTED_RESTORATION_SURFACES[option.value];

		if (option.value === ToothRestorationType.Crown) {
			setSurfaces([ToothSurface.Crown], SurfaceOperation.REPLACE);
		} else if (preselectedSurfaces.length > 0 && !isOriginalType) {
			setSurfaces(preselectedSurfaces, SurfaceOperation.REPLACE);
		}

		setRestoration({
			...(isOriginalType ? originalActiveTooth?.restoration : {}),
			type: option.value,
			surfaces: isOriginalType
				? originalActiveTooth?.restoration?.surfaces ?? []
				: preselectedSurfaces,
		});
		setSelectedOption(option);
	};

	return (
		<DENTModalOverviewButtonsSelection
			title={'patientTreatment.meddiDENT.dentalCross.overviewModal.restoration'}
			onClose={onClose}>
			<ToggleButtons<RestorationOption>
				options={options}
				selectedOption={selectedOption}
				onChange={handleChangeOption}
			/>
			{content}
		</DENTModalOverviewButtonsSelection>
	);
};

export default DENTModalOverviewRestorationButtons;
